import { useMemo } from 'react';
import { formatDistanceStrict } from 'date-fns';
import { AlertTriangle, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { useRescheduleEvent } from '../../hooks/useCalendar';
import { formatInZone } from '../../lib/timezone';

/** What a drag-reschedule will touch, by source — the old copy always said Google. */
function consequence(event) {
    if (event.source === 'coach_booking') return 'This reschedules the booking and emails the guest the new time.';
    const where = { google: 'Google Calendar', outlook: 'Outlook', crm: 'your NurtureCRM event and its calendar copy' }[event.source]
        || 'your calendar';
    return event.attendees?.length
        ? `This will update ${where} and notify ${event.attendees.length} attendee${event.attendees.length === 1 ? '' : 's'}.`
        : `This will update ${where}.`;
}

/**
 * Confirmation after a drag-to-reschedule drop (#679). Times render in the
 * display zone — the old modal used browser-local `format`, so a profile zone
 * far from the browser's showed a different time here than on the grid.
 *
 * Props:
 *   draft: { event, newStart, newEnd } | null
 *   displayZone: string
 *   onCancel(), onRescheduled()
 */
export function RescheduleConfirmModal({ draft, displayZone, onCancel, onRescheduled }) {
    const reschedule = useRescheduleEvent();

    const shift = useMemo(() => {
        if (!draft) return '';
        return formatDistanceStrict(new Date(draft.newStart), new Date(draft.event.start), { addSuffix: true });
    }, [draft]);

    if (!draft) return null;

    const fmt = (d) => formatInZone(d, { zone: displayZone, formatStr: "EEEE, d MMMM '·' h:mm a" });
    const minutes = Math.round((new Date(draft.event.end) - new Date(draft.event.start)) / 60000);

    const handleConfirm = async () => {
        try {
            await reschedule.mutateAsync({ eventId: draft.event.event_id, start: draft.newStart, end: draft.newEnd });
            toast.success('Event rescheduled');
            onRescheduled?.();
        } catch (err) {
            toast.error(err.response?.status === 409 ? 'Time conflict with another event' : 'Failed to reschedule event');
        }
    };

    return (
        <Dialog open={!!draft} onOpenChange={(open) => { if (!open) onCancel(); }}>
            <DialogContent className="sm:max-w-md rounded-[14px] border-[#E9E7E2]" data-testid="reschedule-confirm-modal">
                <DialogHeader>
                    <DialogTitle className="text-base font-semibold text-[#0C0A09]">Reschedule event?</DialogTitle>
                    <DialogDescription className="text-[13px] text-[#57504B]">{draft.event.title}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 text-[13px]">
                    <div className="flex items-start gap-2.5">
                        <Clock className="w-4 h-4 mt-0.5 text-[#8A837E] shrink-0" />
                        <div>
                            <div className="text-[10.5px] font-semibold tracking-[0.09em] uppercase text-[#A29B95]">From</div>
                            <div className="text-[#0C0A09]">{fmt(draft.event.start)}</div>
                        </div>
                    </div>
                    <div className="flex items-start gap-2.5">
                        <Clock className="w-4 h-4 mt-0.5 text-[#4D11F6] shrink-0" />
                        <div>
                            <div className="text-[10.5px] font-semibold tracking-[0.09em] uppercase text-[#A29B95]">To</div>
                            <div className="text-[#0C0A09]">{fmt(draft.newStart)}</div>
                        </div>
                    </div>
                    <p className="text-xs text-[#8A837E] pl-6">{minutes} min, unchanged · moved {shift} · times in {displayZone}</p>
                    <div className="flex items-start gap-2 p-2.5 bg-[#FFFBEB] border border-[#FDE68A] rounded-[9px]">
                        <AlertTriangle className="w-4 h-4 mt-0.5 text-[#B45309] shrink-0" />
                        <p className="text-xs text-[#92400E]">{consequence(draft.event)}</p>
                    </div>
                </div>
                <DialogFooter>
                    <button type="button" onClick={onCancel} className="h-8 px-[13px] rounded-[9px] border border-[#E4E1DB] bg-white text-[12.5px] font-medium text-[#3A3532] hover:bg-[#F4F2EE]" data-testid="reschedule-cancel">
                        Cancel
                    </button>
                    <button type="button" onClick={handleConfirm} disabled={reschedule.isPending} className="inline-flex items-center gap-2 h-8 px-3.5 rounded-[9px] bg-[#4D11F6] text-white text-[12.5px] font-semibold hover:bg-[#3B0DBE] disabled:opacity-60" data-testid="reschedule-confirm">
                        {reschedule.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        Reschedule
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
